/**
 * BBC World Service partner A/V analytics (JavaScript implementation).
 *
 * Spec: https://bbc.github.io/ws-partner-analytics/JavaScriptAV
 *
 * Why this exists:
 * BBC syndicated clips (surfaced on /bbc-urdu) must report playback back to BBC,
 * and — just as importantly — content from every other publisher must NOT. This
 * module is the single owner of the BBC library so callers never touch the
 * global queue directly, and every entry point is a no-op unless the tenant is
 * explicitly configured for BBC reporting.
 *
 * Two deliberate choices:
 *  - The library is injected lazily, on the first BBC clip only. public/index.html
 *    is shared by every white-label tenant (Rockerzs, iScreen, BTV, ...) and none
 *    of them syndicate BBC content, so the script must not ship globally.
 *  - We use the manual `createAvTracker` API rather than `trackHtml5AV`. The player
 *    runs IMA pre-rolls and can also play a trailer through the same <video>
 *    element; auto-wiring would report both as BBC content playback.
 */

const AV_TRACKER_SRC = 'https://static.files.bbci.co.uk/ws-partner-analytics/v5/js/avTracker.min.js';

let initialized = false;
// trackerId -> { started }, so create/track/destroy stay idempotent across
// re-mounts and pause/end can never precede the session's first play.
const trackers = new Map();

/**
 * Per-tenant config, injected alongside the other third-party blocks
 * (window.google_adsense, window.mix_panel, ...) by the platform script.
 * Absent config === BBC reporting disabled for this tenant.
 */
function getConfig() {
  const config = typeof window !== 'undefined' ? window.bbc_analytics : null;
  if (!config || !config.partner || !config.producer) return null;
  return config;
}

/**
 * Readable equivalent of the bootstrap snippet published in the BBC spec:
 * creates the global command queue (so calls made before the script finishes
 * loading are replayed, the same way googletag.cmd works) and injects the
 * library once.
 */
function installBootstrap() {
  const name = (window._ns_bbcws = window._ns_bbcws || 's_bbcws');
  const queueName = `${name}_d`;

  if (!window[name]) {
    window[queueName] = window[queueName] || [];
    window[name] = function () {
      window[queueName].push(arguments);
    };
    window[name].sources = [];
  }

  const sources = window[name].sources;
  if (sources.indexOf(AV_TRACKER_SRC) < 0) {
    const script = document.createElement('script');
    script.async = 1;
    script.src = AV_TRACKER_SRC;
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);
    sources.push(AV_TRACKER_SRC);
  }
}

/** Push a command onto the BBC queue. Safe to call before the library has loaded. */
function send() {
  const name = window._ns_bbcws || 's_bbcws';
  const queue = window[name];
  if (typeof queue !== 'function') return false;
  queue.apply(null, arguments);
  return true;
}

/**
 * One-time global setup. Idempotent.
 * @returns {boolean} false when the tenant has no BBC config (tracking disabled).
 */
export function initBbcAvTracker() {
  if (initialized) return true;

  const config = getConfig();
  if (!config) return false;

  installBootstrap();

  send('partner', config.partner);
  send('producer', config.producer);
  // Optional ISO language code, e.g. 'ur'.
  if (config.language) send('language', config.language);

  initialized = true;
  return true;
}

/**
 * Register one clip. Re-registering an existing trackerId is a no-op.
 *
 * @param {string} trackerId Unique per player instance.
 * @param {{ clipId: string|number, clipName: string, clipDuration: number,
 *           mediaType?: 'video'|'audio', section?: string, player?: string }} clip
 *        clipDuration is whole seconds; mediaType defaults to 'video'.
 * @returns {boolean} whether a tracker is available for this id.
 */
export function createAvTracker(trackerId, clip) {
  if (!trackerId || !clip || !clip.clipId || !clip.clipName) return false;
  if (!initBbcAvTracker()) return false;
  if (trackers.has(trackerId)) return true;

  const payload = {
    clipId: String(clip.clipId),
    clipName: clip.clipName,
    clipDuration: Math.max(0, Math.round(Number(clip.clipDuration) || 0)),
    mediaType: clip.mediaType || 'video'
  };
  if (clip.section) payload.section = clip.section;
  if (clip.player) payload.player = clip.player;

  if (!send('createAvTracker', trackerId, payload)) return false;

  trackers.set(trackerId, { started: false });
  return true;
}

/** Playhead position as a whole-second integer, per spec. */
function toOffset(value) {
  const seconds = Math.floor(Number(value) || 0);
  return seconds > 0 ? seconds : 0;
}

export function trackAvPlay(trackerId, offset) {
  const state = trackers.get(trackerId);
  if (!state) return;
  state.started = true;
  send('track', 'avPlay', trackerId, toOffset(offset));
}

/*
 * avPause and avEnd are only meaningful once a session has started. Playback can
 * legitimately produce a pause with no preceding play — an IMA pre-roll holds the
 * guard closed through the opening play event, and videojs also emits pause when a
 * source fails to load — and BBC treats avPlay as the start of the session, so a
 * leading pause would describe a session that never began.
 */
export function trackAvPause(trackerId, offset) {
  const state = trackers.get(trackerId);
  if (!state || !state.started) return;
  send('track', 'avPause', trackerId, toOffset(offset));
}

export function trackAvEnd(trackerId) {
  const state = trackers.get(trackerId);
  if (!state || !state.started) return;
  state.started = false;
  send('track', 'avEnd', trackerId);
}

/**
 * Drop the tracker on unmount so a later replay registers a fresh one
 * (the spec expects a new session when content is replayed).
 */
export function destroyAvTracker(trackerId) {
  if (!trackerId) return;
  trackers.delete(trackerId);
}
